import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiAlertCircle, FiAlertTriangle, FiClock, FiLock, FiMessageCircle } from "react-icons/fi";
import { useSubscription } from "../../hooks/useSubscription";
import { useUser } from "../../hooks";
import { logout, setSubscriptionStatus } from "../../store/auth/index.js";
import {
  useGetSubscriptionQuery,
  useStartSubscriptionRenewalMutation,
  useVerifySubscriptionRenewalMutation,
} from "../../store/admin/subscriptionSlice";
import { agoLabel, canRenewPlan, describePlan, formatPlanPrice, referenceFromSearch, renewalOptions, renewalUnavailable } from "./subscriptionModel";
import "./subscription.css";

const ICONS = { expired: FiClock, inactive: FiLock, unavailable: FiAlertCircle };

const openSupportChat = () => {
  try {
    window.Tawk_API?.maximize?.();
  } catch (e) {
    /* chat not loaded: the page still explains what to do */
  }
};

/**
 * Shown instead of the page when the cooperative's plan is not active. It says which situation this is,
 * lets a Super Admin renew, and gives everyone else a clear next step. No dead-end buttons.
 *
 * Steps: idle -> choosing -> paying (redirect) ... back here with ?reference= -> verifying -> done | pending | failed.
 */
export default function SubscriptionGate({ className = "" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const user = useUser();
  const { isActive, subscription } = useSubscription();

  const canRenew = canRenewPlan(user?.role);
  const reference = referenceFromSearch(search);

  const [step, setStep] = useState(reference ? "verifying" : "idle");
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");

  const { data: status, isError: statusFailed, error: statusError, refetch } = useGetSubscriptionQuery(undefined, { skip: !canRenew });
  const [startRenewal, { isLoading: starting }] = useStartSubscriptionRenewalMutation();
  const [verifyRenewal] = useVerifySubscriptionRenewalMutation();

  // The server's answer beats what we cached at login. If it says the plan is fine, let them in.
  useEffect(() => {
    if (status && status.is_active === true) dispatch(setSubscriptionStatus(status));
  }, [status, dispatch]);

  const plan = useMemo(() => describePlan(status?.subscription || status || subscription), [status, subscription]);
  const options = useMemo(() => renewalOptions(status), [status]);

  useEffect(() => {
    if (options.length && selected === null) setSelected(options[0].id);
  }, [options, selected]);

  const verified = useRef("");
  const verify = useCallback(
    async (ref) => {
      setStep("verifying");
      try {
        const result = await verifyRenewal(ref).unwrap();
        if (result?.status === "paid") {
          if (result.subscription) dispatch(setSubscriptionStatus({ ...result.subscription, is_active: result.subscription.is_active ?? true }));
          toast.success("Plan renewed. Welcome back.");
          setStep("done");
          navigate(pathname, { replace: true });
        } else if (result?.status === "failed") {
          setMessage(result?.message || "The payment didn't go through, so nothing was charged to your plan.");
          setStep("failed");
        } else {
          setStep("pending");
        }
      } catch (error) {
        setMessage(error?.data?.message || "We couldn't confirm the payment yet.");
        setStep("pending");
      }
    },
    [verifyRenewal, dispatch, navigate, pathname]
  );

  useEffect(() => {
    if (reference && verified.current !== reference) {
      verified.current = reference;
      verify(reference);
    }
  }, [reference, verify]);

  const pay = async () => {
    try {
      const result = await startRenewal({ plan_id: selected, callback_url: `${window.location.origin}${pathname}` }).unwrap();
      if (!result?.authorization_url) throw new Error("No payment link");
      setStep("paying");
      window.location.assign(result.authorization_url);
    } catch (error) {
      if (renewalUnavailable(error)) setStep("unavailable");
      else toast.error(error?.data?.message || "We couldn't start the payment. Try again.");
    }
  };

  if (isActive) return null;

  const Icon = ICONS[plan.state] || FiAlertTriangle;
  const ago = agoLabel(plan.daysAgo);
  const chooseUnavailable = step === "unavailable" || (step === "choosing" && statusFailed && renewalUnavailable(statusError));

  const supportButton = (
    <button type="button" className="sg-btn" onClick={openSupportChat}>
      <FiMessageCircle size={18} aria-hidden="true" /> Chat with support
    </button>
  );

  let content;
  if (step === "verifying") {
    content = (
      <>
        <span className="sg-spin" role="status" aria-label="Confirming your payment" />
        <h1>Confirming your payment</h1>
        <p>We're checking with Paystack. This usually takes a few seconds, so please keep this page open.</p>
      </>
    );
  } else if (step === "pending" || step === "failed") {
    content = (
      <>
        <span className={`sg-icon ${step === "failed" ? "tone-bad" : "tone-warn"}`}>{step === "failed" ? <FiAlertCircle size={30} aria-hidden="true" /> : <FiClock size={30} aria-hidden="true" />}</span>
        <h1>{step === "failed" ? "The payment didn't go through" : "We're still confirming your payment"}</h1>
        <p>{message || (step === "pending" ? "Paystack can take a little while to confirm. If you were charged, your plan renews as soon as it's confirmed." : "")}</p>
        <div className="sg-actions">
          {step === "pending" && reference ? (
            <button type="button" className="sg-btn primary" onClick={() => verify(reference)}>Check again</button>
          ) : (
            <button type="button" className="sg-btn primary" onClick={() => { navigate(pathname, { replace: true }); setStep("choosing"); }}>Try again</button>
          )}
          {supportButton}
        </div>
      </>
    );
  } else if (step === "choosing" && !chooseUnavailable) {
    content = (
      <>
        <span className={`sg-icon tone-${plan.tone}`}><Icon size={30} aria-hidden="true" /></span>
        <h1>Renew your plan</h1>
        {!status && !statusFailed ? (
          <span className="sg-spin" role="status" aria-label="Loading plans" />
        ) : options.length ? (
          <>
            <fieldset className="sg-plans">
              <legend>Choose a plan</legend>
              {options.map((o) => (
                <label key={o.id} className={`sg-plan ${selected === o.id ? "on" : ""}`}>
                  <input type="radio" name="plan" value={o.id} checked={selected === o.id} onChange={() => setSelected(o.id)} />
                  <span className="nm">
                    {o.name}
                    {o.period ? <small>{o.period}</small> : null}
                  </span>
                  <span className="pr">{formatPlanPrice(o.price, o.currency)}</span>
                </label>
              ))}
            </fieldset>
            <div className="sg-actions">
              <button type="button" className="sg-btn" onClick={() => setStep("idle")}>Back</button>
              <button type="button" className="sg-btn primary" onClick={pay} disabled={starting || selected === null}>
                {starting ? "Opening Paystack..." : "Pay with Paystack"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p>We couldn't load the plans to renew. Try again, or chat with support.</p>
            <div className="sg-actions">
              <button type="button" className="sg-btn primary" onClick={() => refetch()}>Try again</button>
              {supportButton}
            </div>
          </>
        )}
      </>
    );
  } else {
    content = (
      <>
        <span className={`sg-icon tone-${plan.tone}`}><Icon size={30} aria-hidden="true" /></span>
        <h1 id="sg-title">{plan.title}</h1>
        <p>{plan.body}</p>

        {plan.planName || plan.expiresLabel ? (
          <dl className="sg-facts" style={{ margin: "8px 0 4px" }}>
            {plan.planName ? (
              <div className="sg-fact"><dt className="l">Plan</dt><dd className="v" style={{ margin: 0 }}>{plan.planName}</dd></div>
            ) : null}
            {plan.expiresLabel ? (
              <div className="sg-fact">
                <dt className="l">{plan.state === "expired" ? "Ended on" : "Ends on"}</dt>
                <dd className={`v ${plan.state === "expired" ? "bad" : ""}`} style={{ margin: 0 }}>{plan.expiresLabel}{ago ? `, ${ago.toLowerCase()}` : ""}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {chooseUnavailable ? (
          <div className="sg-note" role="status">
            <FiAlertTriangle size={18} aria-hidden="true" />
            <span>Online renewal isn't switched on for your account yet. Chat with support and they'll renew it for you.</span>
          </div>
        ) : null}
        {!canRenew ? (
          <div className="sg-note" role="note">
            <FiLock size={18} aria-hidden="true" />
            <span>Only a Super Admin can renew the plan. Ask yours to sign in and renew, or chat with support.</span>
          </div>
        ) : null}

        <div className="sg-actions">
          {canRenew && !chooseUnavailable ? (
            <button type="button" className="sg-btn primary" onClick={() => setStep("choosing")}>Renew plan</button>
          ) : null}
          {supportButton}
        </div>
      </>
    );
  }

  return (
    <section className={`sg ${className}`} aria-labelledby={step === "idle" ? "sg-title" : undefined}>
      <div className="sg-card">
        {content}
        <button type="button" className="sg-link" onClick={() => dispatch(logout())}>Log out</button>
      </div>
    </section>
  );
}
